import { tokens } from '@/theme'

export interface SubNavTab {
  id: string
  label: string
  badge?: number
}

interface Props {
  title: string
  tabs: SubNavTab[]
  active: string
  onSelect: (id: string) => void
}

/**
 * The white second bar under the header, for a section with more than one
 * page: My work (My time, Approvals) and Organisation (People).
 */
export function SubNav({ title, tabs, active, onSelect }: Props) {
  return (
    <div style={{ background: tokens.paper, borderBottom: `1px solid ${tokens.line}` }}>
      <nav
        aria-label={title}
        className="keystone-pad"
        style={{
          maxWidth: tokens.containerMax,
          margin: '0 auto',
          padding: `0 ${tokens.containerPadX}px`,
          display: 'flex',
          alignItems: 'center',
          gap: 22,
          overflowX: 'auto',
          scrollbarWidth: 'none',
        }}
      >
        <span
          style={{
            flex: 'none',
            fontSize: 10.5,
            fontWeight: tokens.weightDemi,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: tokens.gold,
          }}
        >
          {title}
        </span>
        <span style={{ flex: 'none', width: 1, height: 16, background: tokens.line2 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {tabs.map(t => {
            const on = t.id === active
            return (
              <button
                key={t.id}
                type="button"
                aria-current={on ? 'page' : undefined}
                onClick={() => onSelect(t.id)}
                style={{
                  position: 'relative',
                  flex: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '14px 10px',
                  fontFamily: tokens.fontDisplay,
                  fontSize: 13,
                  fontWeight: on ? tokens.weightDemi : tokens.weightMedium,
                  whiteSpace: 'nowrap',
                  color: on ? tokens.ink : tokens.ink3,
                }}
              >
                {t.label}
                {t.badge ? <Badge n={t.badge} /> : null}
                <span
                  style={{
                    position: 'absolute',
                    left: 10,
                    right: 10,
                    bottom: -1,
                    height: 2,
                    background: tokens.ink,
                    transformOrigin: 'left center',
                    transform: on ? 'scaleX(1)' : 'scaleX(0)',
                    transition: `transform 220ms ${tokens.easeStandard}`,
                  }}
                />
              </button>
            )
          })}
        </div>
      </nav>
    </div>
  )
}

/** The gold count on My work and on Approvals. */
export function Badge({ n }: { n: number }) {
  return (
    <span
      aria-label={`${n} pending`}
      style={{
        minWidth: 16,
        height: 16,
        padding: '0 4px',
        boxSizing: 'border-box',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: tokens.gold,
        color: tokens.paper,
        borderRadius: 999,
        fontSize: 9.5,
        fontWeight: tokens.weightDemi,
        letterSpacing: 0,
      }}
    >
      {n}
    </span>
  )
}
